import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Mail,
    Phone,
    MapPin,
    Linkedin,
    Twitter,
    Copy,
    Check,
    ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { PageHero } from "../components/zen/PageHero";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormSuccess,
    FormCard,
    useLeadSubmit,
} from "../components/zen/FormField";
import { FormSubmit } from "../components/zen/FormSubmit";

const CONTACT_EMAIL = "contact@zenmegroup.com";
const CONTACT_PHONE = "+1 832 780 9494";
const CONTACT_PHONE_TEL = "+18327809494";

const FORM_FIELDS = [
    { name: "full_name", required: true, type: "text" },
    { name: "email", required: true, type: "email" },
    { name: "phone", required: false, type: "text" },
    { name: "inquiry_type", required: true, type: "select" },
    { name: "subject", required: true, type: "text" },
    { name: "message", required: true, type: "text" },
];

const DIRECT_LINES = [
    {
        eyebrow: "Engineering",
        title: "BIM Inquiries",
        description: "Multidisciplinary BIM, drafting, and engineering production support.",
        href: "/bim/inquiry",
        testId: "direct-bim",
    },
    {
        eyebrow: "Real Assets",
        title: "Property Submissions",
        description: "Off-market acquisitions across residential and commercial.",
        href: "/real-estate",
        testId: "direct-realestate",
    },
    {
        eyebrow: "Advisory",
        title: "Consulting Engagements",
        description: "Operational, financial, and growth advisory.",
        href: "/consulting",
        testId: "direct-consulting",
    },
];

const CopyableValue = ({ value, label, testId }) => {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 1800);
        } catch (e) {
            toast.error("Copy failed");
        }
    };
    return (
        <button
            type="button"
            onClick={onCopy}
            data-testid={testId}
            className="group inline-flex items-center gap-2 text-text-primary hover:text-[var(--accent)] transition-colors duration-200"
        >
            <span>{value}</span>
            {copied ? (
                <Check
                    className="h-3.5 w-3.5 text-[var(--accent)]"
                    strokeWidth={2}
                />
            ) : (
                <Copy
                    className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100"
                    strokeWidth={1.5}
                />
            )}
        </button>
    );
};

export default function Contact() {
    const form = useLeadSubmit({
        endpoint: "/leads/contact",
        fields: FORM_FIELDS,
    });

    return (
        <PageShell>
            <div data-testid="page-contact">
                <PageHero
                    eyebrow="Contact"
                    title="Get in touch."
                    subtitle="Whether you're expanding your engineering team, seeking partnership, or exploring strategic advisory — we'd be glad to hear from you."
                    data-testid="ct-hero"
                />

                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
                            {/* FORM */}
                            <div className="lg:col-span-7">
                                <Eyebrow>Send a Message</Eyebrow>
                                <Heading as="h2" className="mt-4" fluidSize="h3">
                                    We respond within one business day.
                                </Heading>
                                <div className="mt-8">
                                    <FormCard data-testid="ct-form-card">
                                        {form.success ? (
                                            <FormSuccess
                                                title="Message sent."
                                                message="Thanks for reaching out. We'll get back to you within one business day."
                                                data-testid="ct-form-success"
                                            />
                                        ) : (
                                            <form
                                                onSubmit={form.onSubmit}
                                                noValidate
                                                className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                                                data-testid="ct-form"
                                            >
                                                <FormField
                                                    label="Full Name"
                                                    name="full_name"
                                                    required
                                                    value={form.values.full_name}
                                                    onChange={form.onChange}
                                                    error={form.errors.full_name}
                                                    data-testid="ct-input-name"
                                                />
                                                <FormField
                                                    label="Email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={form.values.email}
                                                    onChange={form.onChange}
                                                    error={form.errors.email}
                                                    data-testid="ct-input-email"
                                                />
                                                <FormField
                                                    label="Phone (optional)"
                                                    name="phone"
                                                    type="tel"
                                                    value={form.values.phone}
                                                    onChange={form.onChange}
                                                    data-testid="ct-input-phone"
                                                />
                                                <FormSelect
                                                    label="Inquiry Type"
                                                    name="inquiry_type"
                                                    required
                                                    value={form.values.inquiry_type}
                                                    onChange={form.onChange}
                                                    error={form.errors.inquiry_type}
                                                    options={[
                                                        "General Inquiry",
                                                        "Partnership",
                                                        "BIM / Engineering",
                                                        "Real Estate",
                                                        "Consulting",
                                                        "Building Materials",
                                                        "Other",
                                                    ]}
                                                    data-testid="ct-input-type"
                                                />
                                                <div className="sm:col-span-2">
                                                    <FormField
                                                        label="Subject"
                                                        name="subject"
                                                        required
                                                        value={form.values.subject}
                                                        onChange={form.onChange}
                                                        error={form.errors.subject}
                                                        data-testid="ct-input-subject"
                                                    />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <FormTextarea
                                                        label="Message"
                                                        name="message"
                                                        required
                                                        rows={6}
                                                        value={form.values.message}
                                                        onChange={form.onChange}
                                                        error={form.errors.message}
                                                        data-testid="ct-input-message"
                                                    />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <FormSubmit
                                                        label="Send Message"
                                                        submitting={form.submitting}
                                                        data-testid="ct-submit-btn"
                                                    />
                                                </div>
                                            </form>
                                        )}
                                    </FormCard>
                                </div>
                            </div>

                            {/* CONTACT INFO CARD */}
                            <aside
                                className="lg:col-span-5"
                                data-testid="ct-info-card"
                            >
                                <div className="rounded-2xl border border-border-subtle bg-elevated p-8 sm:p-10 sticky top-28">
                                    <Eyebrow withDot>Direct Contact</Eyebrow>
                                    <ul className="mt-8 space-y-7">
                                        <li className="flex items-start gap-4">
                                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)]">
                                                <Mail
                                                    className="h-4 w-4 text-[var(--accent)]"
                                                    strokeWidth={1.5}
                                                />
                                            </span>
                                            <div>
                                                <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                                                    Email
                                                </p>
                                                <div className="mt-1 text-[15px]">
                                                    <CopyableValue
                                                        value={CONTACT_EMAIL}
                                                        label="Email"
                                                        testId="ct-copy-email"
                                                    />
                                                </div>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-4">
                                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)]">
                                                <Phone
                                                    className="h-4 w-4 text-[var(--accent)]"
                                                    strokeWidth={1.5}
                                                />
                                            </span>
                                            <div>
                                                <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                                                    Phone
                                                </p>
                                                <div className="mt-1 text-[15px]">
                                                    <a
                                                        href={`tel:${CONTACT_PHONE_TEL}`}
                                                        data-testid="ct-phone-tel-link"
                                                        className="group inline-flex items-center gap-2 text-text-primary hover:text-[var(--accent)] transition-colors duration-200"
                                                    >
                                                        <span>{CONTACT_PHONE}</span>
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(CONTACT_PHONE);
                                                                toast.success("Phone copied");
                                                            } catch (e) {
                                                                toast.error("Copy failed");
                                                            }
                                                        }}
                                                        aria-label="Copy phone number"
                                                        data-testid="ct-copy-phone"
                                                        className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-[var(--accent)] transition-colors duration-200"
                                                    >
                                                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                        <li className="flex items-start gap-4">
                                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)]">
                                                <MapPin
                                                    className="h-4 w-4 text-[var(--accent)]"
                                                    strokeWidth={1.5}
                                                />
                                            </span>
                                            <div>
                                                <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                                                    Office
                                                </p>
                                                <p className="mt-1 text-[15px] text-text-primary">
                                                    Headquarters
                                                </p>
                                                <p className="text-[14px] text-text-muted">
                                                    Address Coming Soon
                                                </p>
                                            </div>
                                        </li>
                                    </ul>

                                    <p className="mt-10 text-[14.5px] leading-[1.65] text-text-secondary">
                                        ZenMe Group LLC — Engineering.
                                        Infrastructure. Execution. We respond
                                        to all inquiries within one business
                                        day.
                                    </p>

                                    <div className="mt-8 flex items-center gap-2 border-t border-border-subtle pt-6">
                                        <a
                                            href="https://www.linkedin.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="LinkedIn"
                                            data-testid="ct-linkedin"
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-muted transition-colors duration-200 hover:text-text-primary hover:border-border-default"
                                        >
                                            <Linkedin
                                                className="h-4 w-4"
                                                strokeWidth={1.5}
                                            />
                                        </a>
                                        <a
                                            href="https://x.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="X / Twitter"
                                            data-testid="ct-twitter"
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-muted transition-colors duration-200 hover:text-text-primary hover:border-border-default"
                                        >
                                            <Twitter
                                                className="h-4 w-4"
                                                strokeWidth={1.5}
                                            />
                                        </a>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    </Container>
                </Section>

                {/* DIRECT LINES */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Direct Lines</Eyebrow>
                            <Heading as="h2" className="mt-4" fluidSize="h3">
                                Route to the right team faster.
                            </Heading>
                        </div>
                        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
                            {DIRECT_LINES.map((line) => (
                                <Link
                                    key={line.title}
                                    to={line.href}
                                    data-testid={line.testId}
                                    className="zen-card group flex h-full flex-col rounded-2xl border border-border-subtle bg-elevated p-7"
                                >
                                    <Eyebrow>{line.eyebrow}</Eyebrow>
                                    <h3 className="mt-5 font-display text-[20px] font-semibold leading-tight text-text-primary">
                                        {line.title}
                                    </h3>
                                    <p className="mt-2 text-[14.5px] leading-[1.65] text-text-secondary">
                                        {line.description}
                                    </p>
                                    <span className="mt-8 inline-flex items-center gap-1.5 text-[14px] font-medium text-text-primary group-hover:text-[var(--accent)] transition-colors duration-300">
                                        Open
                                        <ArrowUpRight
                                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            strokeWidth={1.5}
                                        />
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </Container>
                </Section>
            </div>
        </PageShell>
    );
}
